import { useEffect, useState } from 'react'
import { Button, Input, Grid, Separator } from '@design-systems/canvas-ui'
import _ from 'lodash'
import Icon, {
  PlusCircleIcon,
  MinusCircleIcon,
} from '@design-systems/react-icons'
import { AutocompleteInput } from '../../common/AutocompleteInput'
import {
  renderControlLabel,
  renderControlLabelSmallHeaderOnly,
  showErrorMessage,
} from '../Builders'
import { buildDropdownOptions, getControlError } from '../../common/helper'

const LABEL_CATEGORY = 'Category'
const LABEL_FACTORY_NAME = 'Name of Factory'

const FactoryInput = (props) => {
  const [factoryData, setFactoryData] = useState(props.factoryData)

  useEffect(() => {
    if (!_.isEqual(props.factoryData, factoryData)) {
      const metadata = {
        inputMetadata: {
          groupUID: props.groupUID,
        },
      }
      props.updateFactoryData(factoryData, metadata)
    }
  }, [factoryData]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateFactoryField = (field, newValue, updateIndex) => {
    const newData = factoryData.map((eachFactoryRow, index) =>
      index !== updateIndex
        ? eachFactoryRow
        : { ...eachFactoryRow, [field]: newValue },
    )
    setFactoryData([...newData])
  }

  const renderDesignCentreLocationView = () => {
    return factoryData?.map((eachFactoryRow, factoryIndex) => {
      const formulationFactoryError = getControlError(
        `business_capability.beauty_formulation_capabilities.owned_factories[${factoryIndex}].factory_name`,
        props.errorCache,
      )

      const packagingFactoryError = getControlError(
        `business_capability.beauty_packaging_capabilities.owned_packaging_factories[${factoryIndex}].factory_name`,
        props.errorCache,
      )

      const fieldError =
        (props.groupUID === 'owned_factories' && formulationFactoryError) ||
        (props.groupUID === 'owned_packaging_factories' &&
          packagingFactoryError)

      return (
        <Grid.Container
          key={`factory-row-${factoryIndex}`}
          className="hc8-pl-3x hc8-mb-3x background-grey border-radius"
          spacing="none"
        >
          {/* CATEGORY */}
          <Grid.Item size={{ xs: 3 }} className="hc8-pl-none hc8-pv-2x">
            <div style={{ width: '100%' }}>
              {props.isReadonly
                ? renderControlLabelSmallHeaderOnly({
                    label_name: LABEL_CATEGORY,
                  })
                : renderControlLabel({ label_name: LABEL_CATEGORY })}
              <AutocompleteInput
                id={`tb-factory-category-${factoryIndex}`}
                value={eachFactoryRow.category || ''}
                class="font-size-16 height-32p hc8-mt-2x"
                readOnly={props.isReadonly}
                options={buildDropdownOptions(
                  props.rfiMasterData,
                  { nodeDotPath: 'rfi_supplier.eb_categories' },
                  true,
                  true,
                )}
                onUpdate={(value) => {
                  updateFactoryField('category', value?.value, factoryIndex)
                }}
              />
            </div>
          </Grid.Item>

          {/* FACTORY NAME */}
          <Grid.Item size={{ xs: 3 }} className="hc8-pl-3x hc8-pv-2x">
            <div style={{ width: '100%' }}>
              {props.isReadonly
                ? renderControlLabelSmallHeaderOnly({
                    label_name: LABEL_FACTORY_NAME,
                  })
                : renderControlLabel({ label_name: LABEL_FACTORY_NAME })}

              {props.isReadonly ? (
                <Input.Label
                  id={`tb-factory-name-${factoryIndex}`}
                  className="hc8-mt-2x height-32p"
                >
                  {eachFactoryRow.factory_name || ''}
                </Input.Label>
              ) : (
                <>
                  <Input.Text
                    id={`tb-factory-name-${factoryIndex}`}
                    className="hc8-mt-2x height-32p"
                    value={eachFactoryRow.factory_name || ''}
                    onChange={(event) =>
                      updateFactoryField(
                        'factory_name',
                        event.target.value,
                        factoryIndex,
                      )
                    }
                    isErrored={!!fieldError}
                  />
                  {fieldError && showErrorMessage(fieldError)}
                </>
              )}
            </div>
          </Grid.Item>

          {/* DELETE BUTTON */}
          {!props.isReadonly && (
            <Grid.Item size={{ xs: 1 }} className="align-centre-left">
              <Button
                key={`delete-fi-row-${factoryIndex}`}
                id={`delete-fi-row-${factoryIndex}`}
                data-testid={`delete-fi-row-${factoryIndex}`}
                onClick={() => {
                  const allNewFactories = [...factoryData]
                  allNewFactories.splice(factoryIndex, 1)
                  setFactoryData([...allNewFactories])
                }}
                variant="ghost"
                size="sm"
                isIconOnly
                className="hc8-ml-6x"
                aria-label="Delete factory input row"
              >
                <Icon svg={MinusCircleIcon} aria-hidden />
              </Button>
            </Grid.Item>
          )}
        </Grid.Container>
      )
    })
  }

  return (
    <>
      <Grid.Item>
        <Input.Label className="hc8-fs-150 hc8-mt-none font-weight-500">
          {props.subSectionName}
        </Input.Label>
        <Input.Label className="hc8-mt-4x hc8-fs-100">
          {props.subSectionLabel}
        </Input.Label>
        {renderDesignCentreLocationView()}
        <Separator />
        {!props.isReadonly && (
          <Grid.Item size={{ xs: 4 }} className="hc8-mt-none">
            <Button
              data-testid="btn-add-more-factory"
              variant="ghost"
              className="hc8-pl-none hc8-mb-4x hc8-mt-1x"
              onClick={() =>
                setFactoryData([
                  ...factoryData,
                  { category: null, factory_name: null },
                ])
              }
            >
              <Icon svg={PlusCircleIcon} className="hc8-mr-2x" aria-hidden />
              Add More
            </Button>
          </Grid.Item>
        )}
      </Grid.Item>
    </>
  )
}

export default FactoryInput
